import { Modal } from './ui';
import { Gift, ArrowRight, Check } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { ActiveGiftClaimsModalProps, GiftClaim, isGiftClaimClaimed } from '../types';

function GiftClaimRow({ claim, claimed }: { claim: GiftClaim; claimed: boolean }) {
  return (
    <a
      href={claim.claimUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between gap-4 border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="text-blue-600 mt-0.5 flex-shrink-0">
          <Gift className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900 leading-tight">
            {claim.programName}
          </h3>
          {claim.heading && (
            <p className="mt-1 text-sm text-gray-600">{claim.heading}</p>
          )}
        </div>
      </div>
      {claimed ? (
        <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full flex-shrink-0">
          <Check className="w-3.5 h-3.5" aria-hidden="true" />
          Claimed
        </span>
      ) : (
        <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
      )}
    </a>
  );
}

export function ActiveGiftClaimsModal({ isOpen, onClose }: ActiveGiftClaimsModalProps) {
  const { giftClaims } = useUser();
  const unclaimedGiftClaims = giftClaims.filter((claim) => !isGiftClaimClaimed(claim));
  const claimedGiftClaims = giftClaims.filter((claim) => isGiftClaimClaimed(claim));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Your Gifts" size="lg">
      <div className="px-6 py-4">
        {giftClaims.length === 0 ? (
          <div className="text-center py-12">
            <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Gifts</h3>
            <p className="text-gray-500">There are currently no gifts waiting for you to claim.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {unclaimedGiftClaims.map((claim) => (
              <GiftClaimRow key={claim.publicId} claim={claim} claimed={false} />
            ))}
            {unclaimedGiftClaims.length > 0 && claimedGiftClaims.length > 0 && (
              <hr className="border-gray-200" />
            )}
            {claimedGiftClaims.map((claim) => (
              <GiftClaimRow key={claim.publicId} claim={claim} claimed={true} />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
